/*
 * StationRuntimeSummary.jsx — Station Panel v2 read-only intelligence
 * ──────────────────────────────────────────────────────────────────
 * Adds three UI-only panels that mirror the actual runtime execution
 * order documented in /app/memory/STATION_CONFIG_PANEL_SPEC.md:
 *
 *   1. EffectiveRuntimeConfig  — plain-language summary of what the
 *      backend will actually do given the current form state.
 *   2. IgnoredConfigurationList — per-field reasons why a set value
 *      will NOT participate at runtime.
 *   3. ValidationWarnings      — non-blocking pre-save warnings for
 *      common conflicting or unreachable configurations.
 *
 * Guarantees
 *   - Zero backend calls.
 *   - Zero mutation of the editForm state.
 *   - Pure client-side derivation from editForm.
 *   - No `data-testid` collisions with the existing form.
 *   - Uses only shadcn/lucide primitives already in the codebase.
 */
import React, { useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import {
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Info,
  ArrowRight,
} from 'lucide-react';

/*  ── Pure derivations (mirror backend runtime order — no side effects) ── */

function deriveRuntime(f = {}) {
  const provider = (f.fingerprint_provider || 'auto').toLowerCase();
  const fpEnabled = f.fingerprint_enabled !== false;
  const fpUnlimited = f.fingerprint_unlimited === true;
  const icyEnabled = f.source_icy_enabled !== false;
  const orbEnabled =
    !!f.onlineradiobox_id && f.source_orb_enabled !== false;
  const mytEnabled = !!f.mytuner_id && f.source_mytuner_enabled !== false;
  const websiteEnabled = !!f.website_url;
  const metadataApiEnabled = !!f.metadata_api_url;
  const shazamOnly = provider === 'shazam';
  const providerDisabled = provider === 'disabled';
  const icyRescue = f.icy_stuck_rescue_enabled !== false;

  // Recognition strategy narrative
  let recognitionStrategy = 'Auto (metadata-first, fingerprint fallback)';
  if (shazamOnly) {
    recognitionStrategy = 'SHAZAM-ONLY (all metadata bypassed)';
  } else if (providerDisabled) {
    recognitionStrategy = 'Metadata-only (fingerprint disabled)';
  } else if (fpUnlimited && fpEnabled) {
    recognitionStrategy = 'FINGERPRINT-FIRST (fp runs before metadata)';
  }

  // Metadata source list in runtime order
  const sources = [
    {
      id: 'icy',
      label: 'ICY stream tags',
      set: !!f.stream_url,
      enabled: !shazamOnly && !providerDisabled && icyEnabled,
      overriddenBy: fpUnlimited && !shazamOnly ? 'fingerprint-first' : null,
      requires: f.stream_url ? null : 'stream_url is empty',
    },
    {
      id: 'metadata_api',
      label: 'Custom Metadata API',
      set: metadataApiEnabled,
      enabled: !shazamOnly && !providerDisabled && metadataApiEnabled,
      overriddenBy: fpUnlimited && !shazamOnly ? 'fingerprint-first' : null,
      requires: metadataApiEnabled ? null : 'metadata_api_url is empty',
    },
    {
      id: 'orb',
      label: 'OnlineRadioBox',
      set: !!f.onlineradiobox_id,
      enabled: !shazamOnly && !providerDisabled && orbEnabled,
      overriddenBy: fpUnlimited && !shazamOnly ? 'fingerprint-first' : null,
      requires: f.onlineradiobox_id ? null : 'onlineradiobox_id is empty',
    },
    {
      id: 'mytuner',
      label: 'myTuner',
      set: !!f.mytuner_id,
      enabled: !shazamOnly && !providerDisabled && mytEnabled,
      overriddenBy: fpUnlimited && !shazamOnly ? 'fingerprint-first' : null,
      requires: f.mytuner_id ? null : 'mytuner_id is empty',
    },
    {
      id: 'website',
      label: 'Website scraper',
      set: websiteEnabled,
      enabled: !shazamOnly && !providerDisabled && websiteEnabled,
      overriddenBy: fpUnlimited && !shazamOnly ? 'fingerprint-first' : null,
      requires: websiteEnabled ? null : 'website_url is empty',
    },
  ];

  const monitors =
    f.active !== false &&
    (f.schedule_enabled === false || f.manual_override === true);
  const isActive = f.active !== false;

  return {
    isActive,
    monitors: monitors || isActive,
    recognitionStrategy,
    shazamOnly,
    providerDisabled,
    fpUnlimited,
    fpEnabled,
    icyEnabled,
    icyRescue,
    provider,
    sources,
    persistence: 'Playback Session Writer (WRITER_ENGINE=session)',
    legacy: 'Validator only (writes to shadow metrics)',
  };
}

function deriveIgnored(f = {}, runtime) {
  const items = [];

  if (runtime.shazamOnly) {
    if (f.source_icy_enabled !== false) {
      items.push({
        field: 'ICY Metadata',
        reason: 'SHAZAM-ONLY mode (fingerprint_provider="shazam")',
      });
    }
    if (f.metadata_api_url) {
      items.push({
        field: 'Metadata API',
        reason: 'SHAZAM-ONLY mode bypasses every metadata source',
      });
    }
    if (f.onlineradiobox_id) {
      items.push({
        field: 'OnlineRadioBox',
        reason: 'SHAZAM-ONLY mode bypasses every metadata source',
      });
    }
    if (f.mytuner_id) {
      items.push({
        field: 'myTuner',
        reason: 'SHAZAM-ONLY mode bypasses every metadata source',
      });
    }
    if (f.website_url) {
      items.push({
        field: 'Website scraper',
        reason: 'SHAZAM-ONLY mode bypasses every metadata source',
      });
    }
  }

  if (runtime.providerDisabled && runtime.fpUnlimited) {
    items.push({
      field: 'fingerprint_unlimited',
      reason: 'fingerprint_provider="disabled" — fp cannot run',
    });
  }

  if (
    f.metadata_source_priority &&
    f.metadata_source_priority !== 'auto'
  ) {
    items.push({
      field: 'metadata_source_priority',
      reason:
        'Legacy setting — superseded by the per-source toggles (source_*_enabled)',
    });
  }

  if (
    f.source_orb_enabled &&
    !f.onlineradiobox_id
  ) {
    items.push({
      field: 'source_orb_enabled',
      reason: 'onlineradiobox_id is empty — ORB cannot fetch anything',
    });
  }
  if (f.source_mytuner_enabled && !f.mytuner_id) {
    items.push({
      field: 'source_mytuner_enabled',
      reason: 'mytuner_id is empty — myTuner cannot fetch anything',
    });
  }
  if (f.fingerprint_unlimited && !f.stream_url) {
    items.push({
      field: 'fingerprint_unlimited',
      reason: 'stream_url is empty — cannot capture audio for fingerprint',
    });
  }
  if (f.data_mode === 'stream' && f.stats_url) {
    items.push({
      field: 'stats_url',
      reason:
        'data_mode="stream" — listener stats are signal-based; stats_url is not consulted',
    });
  }
  if (f.include_in_chart && f.active === false) {
    items.push({
      field: 'include_in_chart',
      reason: 'Station is inactive — chart excludes non-polling stations',
    });
  }

  return items;
}

function deriveWarnings(f = {}, runtime) {
  const warnings = [];

  if (!f.stream_url && !f.metadata_api_url && !f.onlineradiobox_id &&
      !f.mytuner_id && !f.website_url) {
    warnings.push({
      severity: 'error',
      message:
        'No identity source configured — station cannot identify any song.',
    });
  }

  if (runtime.shazamOnly && !f.stream_url) {
    warnings.push({
      severity: 'error',
      message:
        'SHAZAM-ONLY mode requires stream_url for audio capture.',
    });
  }

  if (
    runtime.fpUnlimited &&
    runtime.providerDisabled === false &&
    !f.stream_url
  ) {
    warnings.push({
      severity: 'warning',
      message:
        'Fingerprint-first is on but stream_url is empty — fp will silently skip every cycle.',
    });
  }

  if (runtime.shazamOnly && (
    f.source_icy_enabled !== false || f.metadata_api_url ||
    f.onlineradiobox_id || f.mytuner_id || f.website_url
  )) {
    warnings.push({
      severity: 'info',
      message:
        'SHAZAM-ONLY mode force-disables every metadata source. Configured sources will be ignored at runtime.',
    });
  }

  if (f.include_in_chart && f.active === false) {
    warnings.push({
      severity: 'warning',
      message:
        'include_in_chart is on but the station is inactive — it will not appear in the chart.',
    });
  }

  if (
    f.metadata_source_priority &&
    f.metadata_source_priority !== 'auto' &&
    (f.source_icy_enabled !== undefined ||
      f.source_orb_enabled !== undefined ||
      f.source_mytuner_enabled !== undefined)
  ) {
    warnings.push({
      severity: 'info',
      message:
        'metadata_source_priority is a legacy setting — the per-source toggles above take precedence.',
    });
  }

  if (
    f.poll_interval_seconds &&
    f.poll_interval_seconds < 15 &&
    f.fingerprint_unlimited
  ) {
    warnings.push({
      severity: 'warning',
      message:
        'Poll interval < 15s with fingerprint-first may exceed the recognition budget — monitor Station Diagnostic.',
    });
  }

  return warnings;
}

/* ── Panels ────────────────────────────────────────────────────── */

function SourceBadge({ src }) {
  let status = 'Unavailable';
  let color = 'bg-gray-100 text-gray-500 border-gray-300 dark:bg-gray-800 dark:text-gray-400';
  if (!src.set) {
    status = 'Not configured';
  } else if (!src.enabled) {
    status = 'Disabled';
    color = 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800';
  } else if (src.overriddenBy) {
    status = `Fallback (overridden by ${src.overriddenBy})`;
    color = 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800';
  } else {
    status = 'Enabled';
    color = 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800';
  }
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${color}`}
      data-testid={`runtime-source-badge-${src.id}`}
    >
      {status}
    </span>
  );
}

export default function StationRuntimeSummary({ editForm }) {
  const runtime = useMemo(() => deriveRuntime(editForm || {}), [editForm]);
  const ignored = useMemo(
    () => deriveIgnored(editForm || {}, runtime),
    [editForm, runtime],
  );
  const warnings = useMemo(
    () => deriveWarnings(editForm || {}, runtime),
    [editForm, runtime],
  );

  return (
    <div
      className="space-y-3"
      data-testid="station-runtime-summary-root"
    >
      {/* ── Validation Warnings ────────────────────────────── */}
      {warnings.length > 0 && (
        <Card
          className="border-2 border-amber-300/70 dark:border-amber-600/40 bg-amber-50/60 dark:bg-amber-900/10"
          data-testid="station-validation-warnings-card"
        >
          <CardContent className="pt-4 space-y-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                Configuration Warnings
              </h4>
            </div>
            <ul className="space-y-1.5">
              {warnings.map((w, i) => (
                <li
                  key={i}
                  data-testid={`station-warning-${w.severity}-${i}`}
                  className={`text-xs flex items-start gap-2 ${
                    w.severity === 'error'
                      ? 'text-red-700 dark:text-red-400'
                      : w.severity === 'warning'
                      ? 'text-amber-700 dark:text-amber-400'
                      : 'text-blue-700 dark:text-blue-400'
                  }`}
                >
                  {w.severity === 'error' ? (
                    <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  ) : w.severity === 'warning' ? (
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  ) : (
                    <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  )}
                  <span>{w.message}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* ── Effective Runtime Configuration ──────────────────── */}
      <Card
        className="border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10"
        data-testid="station-effective-runtime-card"
      >
        <CardContent className="pt-4 space-y-3">
          <div className="flex items-center gap-2">
            <ArrowRight className="w-4 h-4 text-blue-600" />
            <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-300">
              Effective Runtime Configuration
            </h4>
            <span className="text-[10px] text-gray-500 dark:text-gray-400">
              (read-only projection)
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div>
              <div className="font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Recording
              </div>
              <div className="flex items-center gap-2">
                {runtime.isActive ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                ) : (
                  <XCircle className="w-3.5 h-3.5 text-red-600" />
                )}
                <span
                  className={
                    runtime.isActive
                      ? 'text-emerald-700 dark:text-emerald-400'
                      : 'text-red-700 dark:text-red-400'
                  }
                  data-testid="runtime-recording-status"
                >
                  {runtime.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <div>
              <div className="font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Recognition Strategy
              </div>
              <div
                className="text-gray-800 dark:text-gray-200"
                data-testid="runtime-recognition-strategy"
              >
                {runtime.recognitionStrategy}
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400">
                Provider: {runtime.provider}
              </div>
            </div>

            <div className="md:col-span-2">
              <div className="font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Metadata Sources (runtime priority order)
              </div>
              <ol className="space-y-1">
                {runtime.sources.map((src, i) => (
                  <li
                    key={src.id}
                    className="flex items-center justify-between text-xs"
                    data-testid={`runtime-source-row-${src.id}`}
                  >
                    <span className="text-gray-800 dark:text-gray-200">
                      <span className="font-mono text-gray-500">
                        {i + 1}.
                      </span>{' '}
                      {src.label}
                    </span>
                    <SourceBadge src={src} />
                  </li>
                ))}
              </ol>
              {runtime.icyEnabled && (
                <div className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">
                  ICY-Stuck Rescue:{' '}
                  <span className="font-medium">
                    {runtime.icyRescue ? 'ON' : 'OFF'}
                  </span>
                </div>
              )}
            </div>

            <div>
              <div className="font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Persistence
              </div>
              <div className="text-gray-800 dark:text-gray-200">
                {runtime.persistence}
              </div>
            </div>

            <div>
              <div className="font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Legacy
              </div>
              <div className="text-gray-800 dark:text-gray-200">
                {runtime.legacy}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Ignored Configuration ────────────────────────────── */}
      {ignored.length > 0 && (
        <Card
          className="border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40"
          data-testid="station-ignored-configuration-card"
        >
          <CardContent className="pt-4 space-y-2">
            <div className="flex items-center gap-2">
              <Info className="w-4 h-4 text-gray-500" />
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Ignored Configuration
              </h4>
              <span className="text-[10px] text-gray-500">
                (values set but not consulted at runtime)
              </span>
            </div>
            <ul className="space-y-1">
              {ignored.map((it, i) => (
                <li
                  key={i}
                  className="flex items-start justify-between gap-3 text-xs border border-gray-200 dark:border-gray-700 rounded-md p-2 bg-white dark:bg-gray-950"
                  data-testid={`station-ignored-item-${i}`}
                >
                  <div>
                    <div className="font-medium text-gray-700 dark:text-gray-300">
                      {it.field}
                    </div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400">
                      Reason: {it.reason}
                    </div>
                  </div>
                  <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 flex-shrink-0 self-center">
                    IGNORED
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
